import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';

import { COLORS } from '../../IndividualPlan/tokens';
import type { PeEnrollment, PeReclassification } from '../types';
import { CountLink } from './CountLink';

interface Stat {
  key: string;
  title: string;
  value: ReactNode;
  caption?: string;
}

function signed(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}

function netChangeCaption(netChange: number): string {
  if (netChange > 0) return 'enrollment growth';
  if (netChange < 0) return 'enrollment decline';
  return 'no net change';
}

function buildStats(
  enrollment: PeEnrollment,
  reclassification: PeReclassification,
  newcomerMaxYears: number,
): Stat[] {
  const newNewcomers = enrollment.newcomers_among_new_identifications ?? null;
  const served = enrollment.el_count_this_year;
  const inProgram = enrollment.in_program_now;
  const servedCaption =
    inProgram != null
      ? `${inProgram} in program now + ${served - inProgram} exited`
      : enrollment.net_change === null
        ? undefined
        : `${signed(enrollment.net_change)} vs last year`;
  void reclassification;

  return [
    {
      key: 'total-served',
      title: 'Total Served',
      value: <CountLink count={served} listKey="served" />,
      caption: servedCaption,
    },
    {
      key: 'newcomers',
      title: 'Newcomers',
      value: <CountLink count={enrollment.newcomer_count} listKey="newcomer" />,
      caption: `first ${newcomerMaxYears} years classified EL`,
    },
    {
      key: 'newly-identified',
      title: 'Newly Identified',
      value: <CountLink count={enrollment.new_identifications_this_year} listKey="newid" />,
      caption:
        newNewcomers === null
          ? undefined
          : `including ${newNewcomers} newcomer${newNewcomers === 1 ? '' : 's'}`,
    },
    {
      key: 'net-change',
      title: 'Net Change',
      value: enrollment.net_change === null ? '—' : signed(enrollment.net_change),
      caption: enrollment.net_change === null ? undefined : netChangeCaption(enrollment.net_change),
    },
  ];
}

export interface EnrollmentStatCardsProps {
  enrollment: PeEnrollment;
  reclassification: PeReclassification;
  newcomerMaxYears: number;
}

export function EnrollmentStatCards({
  enrollment,
  reclassification,
  newcomerMaxYears,
}: EnrollmentStatCardsProps) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
        gap: 2,
      }}
    >
      {buildStats(enrollment, reclassification, newcomerMaxYears).map((stat) => (
        <Box
          key={stat.key}
          data-stat-card={stat.key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            height: 112,
            p: 2,
            borderRadius: '8px',
            backgroundColor: COLORS.bg,
          }}
        >
          <Typography
            component="h4"
            sx={{
              fontSize: 16,
              lineHeight: '24px',
              fontWeight: 700,
              color: COLORS.muted,
            }}
          >
            {stat.title}
          </Typography>
          <Box>
            <Typography
              sx={{
                fontSize: 32,
                lineHeight: '40px',
                fontWeight: 600,
                color: COLORS.ink,
              }}
            >
              {stat.value}
            </Typography>
            {stat.caption && (
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: '16px',
                  fontWeight: 500,
                  color: COLORS.muted,
                }}
              >
                {stat.caption}
              </Typography>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
